import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import type * as prismic from '@prismicio/client';
import DynamicMarquee from '@/components/DynamicMarquee';
import clsx from 'clsx';
import {
  StatsSliceDefault,
  StatsSliceDefaultPrimaryBadgeItem,
  StatsSliceDefaultPrimaryStatsItem,
  StatsSliceForSubpage,
  StatsSliceOnlyBadges,
  StatsSliceWithCustomBackground,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { Carousel } from '@/components';
import marqueeStyles from '@/styles/marquee.module.css';

enum SLICE_VARIATION {
  default = 'default',
  forSubpage = 'forSubpage',
  onlyBadges = 'onlyBadges',
  withCustomBackground = 'withCustomBackground',
}

/**
 * Props for `Stats`.
 */
export type StatsProps = SliceComponentProps<Content.StatsSlice>;
export type DefaultVariation = prismic.SharedSlice<'stats', StatsSliceDefault>;
export type ForSubpageVariation = prismic.SharedSlice<
  'stats',
  StatsSliceForSubpage
>;

/**
 * Component for "Stats" Slices.
 */
const Stats = ({ slice }: StatsProps): JSX.Element => {
  const getClassByVariation = (variation: string) => {
    switch (variation) {
      case SLICE_VARIATION.withCustomBackground:
        return 'bg-gradient-to-b from-white via-white via-70% to-[#B6F6EA]\n';
      default:
        return 'text-white px-5 md:px-16 max-w-screen-2xl mx-auto';
    }
  };

  const styledClass = getClassByVariation(slice.variation);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styledClass}
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.forSubpage &&
        forSubPageVariation(slice)}
      {slice.variation === SLICE_VARIATION.onlyBadges && onlyBadges(slice)}
      {slice.variation === SLICE_VARIATION.withCustomBackground &&
        withCustomBackground(slice)}
    </section>
  );
};

const defaultVariation = (slice: DefaultVariation) => {
  return (
    <div className="rounded-[20px] border-solid border-primary-shade-green/50 border-[1px] shadow-green-shadow backdrop-blur-[5.7px] bg-black/5 px-[30px] md:px-16 py-[40px] md:py-28">
      <h2 className="text-h3">{slice.primary.title}</h2>
      <p className="md:text-[20px] leading-6 my-4">
        {slice.primary.description}
      </p>
      <div className="md:hidden">
        <Carousel
          autoPlaySpeed={5000}
          autoPlay
          renderDotsOutside
          dotType="compact-dark"
          dotExtraClass="static mt-4 mb-[53px]"
        >
          {slice.primary.stats.map((stat, index) => (
            <div
              key={`stats_mobile_wrapper_${index.toString()}`}
              className="px-1"
            >
              {renderStatCard(stat, `stats_mobile_${index.toString()}`)}
            </div>
          ))}
        </Carousel>
        <DynamicMarquee autoFill speed={85} className="flex flex-row">
          {slice.primary.badge.map((badge, index) =>
            renderBadge(badge, `badge_mobile${index.toString()}`),
          )}
        </DynamicMarquee>
      </div>
      <div className="hidden md:inline-block w-full overflow-hidden">
        <div className="flex gap-8 w-full flex-row justify-between flex-wrap my-20">
          {slice.primary.stats.map((stat, index) =>
            renderStatCard(stat, `stats_desktop_${index.toString()}`),
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-20 justify-between">
          {slice.primary.badge.map((badge, index) =>
            renderBadge(badge, `badge_desktop_${index.toString()}`),
          )}
        </div>
      </div>
    </div>
  );
};

const forSubPageVariation = (slice: ForSubpageVariation) => {
  return (
    <div className="flex gap-8 w-full flex-row justify-center flex-wrap my-20">
      {slice.primary.stats.map((stat, index) =>
        renderStatCard(
          stat,
          `stats_desktop_${index.toString()}`,
          slice.variation,
        ),
      )}
    </div>
  );
};

const onlyBadges = (slice: StatsSliceOnlyBadges) => {
  return (
    <div className="rounded-[20px] border-solid border-primary-shade-green/50 border-[1px] shadow-green-shadow backdrop-blur-[5.7px] bg-black/5 px-[30px] md:px-16 py-[40px] md:py-28">
      <PrismicRichText field={slice.primary.title} />
      <PrismicNextLink
        field={slice.primary.button_link}
        className="button-outline-primary mt-4 mb-[50px]"
      >
        <span>{slice.primary.button_label}</span>
      </PrismicNextLink>
      <div>
        <div className="lg:hidden">
          <DynamicMarquee autoFill speed={85} className="flex flex-row">
            {slice.primary.badge.map((badge, index) =>
              renderBadge(badge, `badge_mobile${index.toString()}`),
            )}
          </DynamicMarquee>
        </div>
        <div className="hidden lg:inline-block w-full overflow-hidden">
          <div className="flex justify-between flex-wrap gap-x-4 gap-y-20">
            {slice.primary.badge.map((badge, index) =>
              renderBadge(badge, `badge_desktop_${index.toString()}`),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const renderStatCard = (
  { value, description }: StatsSliceDefaultPrimaryStatsItem,
  key: string,
  variation: string = 'default',
  className: string = '',
): JSX.Element => {
  return (
    <div
      key={key}
      className={clsx(
        'md:w-52 md:h-40 md:shrink-0 md:shadow-lg md:shadow-black/25 rounded-[20px] mb-1 backdrop-blur-[47.5px] p-6 h-full',
        variation === 'forSubpage'
          ? marqueeStyles.marqueeBlurredBorder
          : 'border-solid border-white/30 border-[1px]',
        className === '' ? 'bg-stats-gradient' : className,
      )}
    >
      <p className="text-primary-darker-green text-4xl font-bold mb-4">
        {value}
      </p>
      <p className="text-[13px]">{description}</p>
    </div>
  );
};

const renderBadge = (
  { image }: StatsSliceDefaultPrimaryBadgeItem,
  key: string,
  className: string = '',
): JSX.Element => {
  return (
    <div
      key={key}
      className={`${className} ? ${className} : lg:w-[250px] flex justify-center`}
    >
      <PrismicNextImage
        field={image}
        className="flex-grow-0 self-center object-contain h-[149px] mx-[20px] max-w-[149px]"
      />
    </div>
  );
};

const withCustomBackground = (slice: StatsSliceWithCustomBackground) => {
  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full mx-auto flex flex-col md:flex-row">
        <div className="flex-1 space-y-4 px-5 py-16 md:px-16 md:py-12">
          <h1 className="text-2xl md:text-[40px] font-bold text-gray-900 leading-tight">
            {slice.primary.title}
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl">
            {slice.primary.description}
          </p>
        </div>
      </div>

      <div className="hidden md:inline-block flex-1 px-5 py-16 md:px-16 md:py-1 md:mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-10">
          {slice.primary.stats.map((stat, index) => {
            const value = stat.value || '';
            const mainValue = value.slice(0, -1);
            const lastChar = value.slice(-1);
            return (
              <div
                key={index}
                className="flex flex-col items-center justify-center text-center"
              >
                <div className="flex items-center justify-center">
                  <span className="text-4xl md:text-5xl font-bold text-gray-800">
                    {mainValue}
                  </span>
                  <span className="text-4xl md:text-5xl font-bold text-teal-500">
                    {lastChar}
                  </span>
                </div>
                <p className="text-[14px] text-black mt-2">
                  {stat.description}
                </p>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-10">
          {slice.primary.badge.map((badge, index) => {
            return (
              <div key={index} className="flex flex-col">
                <div className="flex items-center">
                  {renderBadge(
                    badge,
                    `badge_desktop_${index.toString()}`,
                    'w-32 h-32',
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="block md:hidden">
        <Carousel
          autoPlaySpeed={5000}
          autoPlay
          renderDotsOutside
          dotType="compact-dark"
          dotExtraClass="static flex justify-center"
        >
          {slice.primary.stats.map((stat, index) => {
            const value = stat.value || '';
            const mainValue = value.slice(0, -1);
            const lastChar = value.slice(-1);
            return (
              <div
                key={`stats_mobile_wrapper_${index.toString()}`}
                className="px-1"
              >
                <div className="h-full bg-transparent text-center">
                  <span className="text-4xl font-bold text-gray-800">
                    {mainValue}
                  </span>
                  <span className="text-4xl font-bold text-teal-500">
                    {lastChar}
                  </span>
                  <p className="text-[13px] text-black mt-2">
                    {stat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </Carousel>
        <DynamicMarquee autoFill speed={85} className="flex flex-row mt-4">
          {slice.primary.badge.map((badge, index) =>
            renderBadge(badge, `badge_mobile${index.toString()}`),
          )}
        </DynamicMarquee>
      </div>
    </div>
  );
};

export default Stats;
